// Pengaturan Website (mig 083) — helper murni & tipe untuk setelan toko
// (key-value `site_settings`). Tanpa Supabase di sini supaya mudah diuji.

import { normalizePhone, isValidPhone, ADMIN_WHATSAPP } from "@/lib/config";
import {
  SHIPPING_SETTING_KEYS,
  parseShippingOriginSettings,
} from "@/lib/biteship";
import { PIXEL_SETTING_KEYS } from "@/lib/pixel";
import {
  normalizeDropshipMarkupMin,
  normalizeDropshipStockBuffer,
} from "@/lib/dropshipShopee";
import {
  MULTI_WAREHOUSE_SETTING_KEY,
  parseMultiWarehouseFlag,
} from "@/lib/multiWarehouse";
import { coercePrintMode, type PrintMode } from "@/lib/printModes";

/** Key setelan yang dipakai kode (cermin baris seed mig 083/097/102/3720). */
export const SITE_SETTING_KEYS = {
  whatsappNumber: "whatsapp_number",
  storeName: "store_name",
  storeLogoUrl: "store_logo_url",
  storePwaIcons: "store_pwa_icons",
  // Token verifikasi Google Search Console PER TOKO. Tiap penyewa berjalan di
  // domainnya sendiri, jadi verifikasinya juga per-domain — tanpa field ini
  // pemilik toko tak punya cara memverifikasi domainnya (ia tak pegang DNS
  // maupun berkas root) dan karenanya tak bisa menyubmit sitemap-nya sendiri.
  googleSiteVerification: "google_site_verification",
  // Kode verifikasi domain Meta (Katalog Instagram, PR-IG-2) PER TOKO — meta
  // `facebook-domain-verification` di domain toko itu. SENGAJA publik-baca
  // (tidak di denylist `site_setting_is_internal`, mig 3550): nilainya memang
  // tercetak di HTML setiap halaman toko.
  metaDomainVerification: "meta_domain_verification",
  // Judul & deskripsi SITUS di hasil pencarian. Kosong = rumus bawaan
  // (`storeSeoTitle`/`storeSeoDescription` di seo.ts) — toko yang tak mengisi
  // apa pun terbit persis seperti sebelum fitur ini ada.
  seoTitle: "seo_title",
  seoDescription: "seo_description",
  keepExpiryEnabled: "keep_expiry_enabled",
  keepExpiryDays: "keep_expiry_days",
  invoiceDueEnabled: "invoice_due_enabled",
  invoiceDueDays: "invoice_due_days",
  // Nota pengajuan pembeli langsung terbit tanpa Perlu ACC (mig 4320).
  // Token "hidup"-nya WAJIB sama dengan `invoice_auto_acc_enabled(p_store)`
  // di SQL — dua pembaca yang tak sepakat berarti admin melihat toggle
  // NYALA sementara notanya tetap menumpuk di papan Perlu ACC.
  invoiceAutoAccEnabled: "invoice_auto_acc_enabled",
  dropshipFeeEnabled: "dropship_fee_enabled",
  dropshipFeeAmount: "dropship_fee_amount",
  dashboardAlertPiutang: "dashboard_alert_piutang",
  dashboardAlertClosedPoNoOrder: "dashboard_alert_closed_po_no_order",
  vapidPublicKey: "vapid_public_key",
  archivedPhotoPurgeEnabled: "archived_photo_purge_enabled",
  archivedPhotoPurgeDays: "archived_photo_purge_days",
  resellerCatalogEnabled: "reseller_catalog_enabled",
  registrationAutoActive: "registration_auto_active",
  // Mode default kartu Biaya Produksi (mig 4250) — "supplier" | "produksi".
  // Tiap produk masih bisa menimpanya lewat products.production_cost_mode.
  productionCostMode: "production_cost_mode",
  // Mode cetak BAWAAN Detail Tagihan & Surat Jalan (mig 7400) — "regular" |
  // "thermal". Absen = Printer Biasa, perilaku sebelum struk thermal ada.
  printModeInvoice: "print_mode_invoice",
  printModePackingList: "print_mode_packing_list",
  // Direktori Mitra Terdekat (mig 4230). Token "mati" ketiganya WAJIB sama
  // dengan SQL (`false/0/off/no`) — kill-switch dibaca DUA pihak (RPC dan layar
  // admin), dan daftar token yang menyimpang adalah kelas bug "dua pembaca tak
  // sepakat" yang sudah tercatat pada registrationAutoActive di bawah.
  partnerDirectoryEnabled: "partner_directory_enabled",
  partnerProductCtaEnabled: "partner_product_cta_enabled",
  partnerDirectoryStatsEnabled: "partner_directory_stats_enabled",
  // Tombol "Order via WhatsApp" halaman produk untuk pengunjung yang BELUM
  // login. Sekelompok dengan partner_product_cta_enabled di atas: dua-duanya
  // CTA tamu di halaman yang sama, dibaca sesi ANON — karena itu keduanya
  // wajib TETAP di luar denylist `site_setting_is_internal` (mig 3550/4730),
  // kalau masuk ke sana saklarnya tak terbaca pengunjung dan tombolnya mati
  // di semua toko tanpa satu pun error.
  guestWaOrderEnabled: "guest_wa_order_enabled",
  // Izin toko lain meng-clone produk toko ini lewat "Tambah Produk dari Link"
  // (mig 5850). Token "mati"-nya dibaca DUA pihak — `flagOn` di bawah dan
  // `store_flag_on` di SQL — jadi daftarnya wajib identik, sama seperti ketiga
  // bendera direktori mitra di atas.
  productCloneAllowed: "product_clone_allowed",
  // Sisa PO otomatis jadi Ready Stok (mig 7380). Dibaca DUA pihak — `flagOn`
  // di bawah dan `po_surplus_auto_ready_enabled()` (store_flag_on) di SQL —
  // jadi token "mati"-nya wajib identik; bawaan NYALA di keduanya.
  poSurplusAutoReady: "po_surplus_auto_ready",
  // Cek ongkir otomatis (mig 3720). Nama key-nya SENGAJA diambil dari
  // `SHIPPING_SETTING_KEYS` (biteship.ts) alih-alih ditulis ulang sebagai
  // literal: modul itu SUDAH memegang daftar yang sama untuk sisi server
  // (`readOriginSettings` men-`.in()`-kannya), dan dua salinan string key yang
  // menyimpang akan membuat layar admin menulis ke key yang tak pernah dibaca
  // route — fitur mati senyap tanpa satu pun error.
  shippingRateEnabled: SHIPPING_SETTING_KEYS.enabled,
  shippingOriginAreaId: SHIPPING_SETTING_KEYS.originAreaId,
  shippingOriginDistrictKey: SHIPPING_SETTING_KEYS.originDistrictKey,
  shippingOriginLabel: SHIPPING_SETTING_KEYS.originLabel,
  shippingCouriers: SHIPPING_SETTING_KEYS.couriers,
  shippingWeightToleranceGram: SHIPPING_SETTING_KEYS.weightToleranceGram,
  // Saklar Multigudang (mig 7200) — nama key dari `MULTI_WAREHOUSE_SETTING_KEY`
  // (multiWarehouse.ts), alasan yang sama dengan SHIPPING_SETTING_KEYS: route
  // tarif membaca key yang sama di sisi server. Publik-baca (bukan rahasia;
  // pembeli toh melihat "Dikirim dari …").
  multiWarehouseEnabled: MULTI_WAREHOUSE_SETTING_KEY,
  // Pixel iklan per-toko — nama key dari `PIXEL_SETTING_KEYS` (pixel.ts),
  // alasan yang sama dengan SHIPPING_SETTING_KEYS di atas: pembaca server
  // (pixelSettingsServer.ts) memakai daftar yang sama, dua salinan string
  // yang menyimpang = layar admin menulis ke key yang tak pernah dibaca
  // storefront. Key ini PUBLIK by-design (ID toh tampil di HTML) — JANGAN
  // dimasukkan denylist `site_setting_is_internal` (mig 3550/3870).
  metaPixelId: PIXEL_SETTING_KEYS.metaPixelId,
  tiktokPixelId: PIXEL_SETTING_KEYS.tiktokPixelId,
  ga4MeasurementId: PIXEL_SETTING_KEYS.ga4MeasurementId,
  gtmContainerId: PIXEL_SETTING_KEYS.gtmContainerId,
  // Banner privasi (UU PDP) — publik-baca, pengunjung anon yang merendernya.
  privacyBannerEnabled: "privacy_banner_enabled",
  privacyBannerText: "privacy_banner_text",
  // Dropship Shopee untuk MITRA (mig 4720). Token "hidup" toggle-nya WAJIB sama
  // dengan `dropship_shopee_access()` di SQL (`true/1/on/yes`) — dua pembaca
  // yang tak sepakat = admin melihat toggle NYALA sementara route mitra
  // menjawab 404. BUKAN `dropship_fee_*` (biaya packing) — fitur berbeda.
  dropshipShopeeEnabled: "dropship_shopee_enabled",
  dropshipShopeeStockBuffer: "dropship_shopee_stock_buffer",
  dropshipShopeeMarkupMin: "dropship_shopee_markup_min",
  dropshipShopeeMitraOnly: "dropship_shopee_mitra_only",
} as const;

/**
 * Setelan yang MEMBAWA IDENTITAS TOKO INDUK — tak pernah diunduh browser di
 * katalog reseller `/toko/<kode>` (white-label; permintaan pemilik 2026-10-30:
 * pembeli reseller tak boleh bisa tahu distributornya, termasuk lewat tab
 * jaringan). Katalog tak memakai satu pun nilainya: nama & WA yang tampil milik
 * reseller, logo toko induk sengaja disembunyikan, ongkir katalog dihitung
 * server dan hanya memulangkan JUMLAH lokasi asal. Nilai yang absen jatuh ke
 * bawaan netral `DEFAULT_SITE_SETTINGS` (nama "Kelolify", teks banner privasi
 * bawaan) — teks banner kustom ikut di sini karena ditulis admin dan bisa
 * menyebut nama tokonya.
 */
export const CATALOG_HIDDEN_SETTING_KEYS: readonly string[] = [
  SITE_SETTING_KEYS.whatsappNumber,
  SITE_SETTING_KEYS.storeName,
  SITE_SETTING_KEYS.storeLogoUrl,
  SITE_SETTING_KEYS.storePwaIcons,
  SITE_SETTING_KEYS.googleSiteVerification,
  SITE_SETTING_KEYS.metaDomainVerification,
  SITE_SETTING_KEYS.seoTitle,
  SITE_SETTING_KEYS.seoDescription,
  SITE_SETTING_KEYS.privacyBannerText,
  SITE_SETTING_KEYS.shippingOriginAreaId,
  SITE_SETTING_KEYS.shippingOriginDistrictKey,
  SITE_SETTING_KEYS.shippingOriginLabel,
];

/** Key `site_settings` yang diunduh `fetchSiteSettings` — katalog tanpa identitas toko. */
export function siteSettingKeysFor(catalog: boolean): string[] {
  const all = Object.values(SITE_SETTING_KEYS) as string[];
  return catalog ? all.filter((key) => !CATALOG_HIDDEN_SETTING_KEYS.includes(key)) : all;
}

/** Pilihan lama keep (hari) untuk ready stock — cermin opsi di UI. */
export const KEEP_EXPIRY_DAY_OPTIONS = [1, 3, 7, 14, 30, 60] as const;
export type KeepExpiryDays = (typeof KEEP_EXPIRY_DAY_OPTIONS)[number];

/**
 * Pilihan tempo bayar nota (hari, mig 4260). Sengaja lebih rapat dan lebih
 * pendek daripada opsi keep: tempo nota mengukur "berapa lama sabar menunggu
 * transfer", bukan "berapa lama barang boleh dititip".
 */
export const INVOICE_DUE_DAY_OPTIONS = [1, 2, 3, 5, 7, 14] as const;
export type InvoiceDueDays = (typeof INVOICE_DUE_DAY_OPTIONS)[number];

/**
 * Pilihan lama arsip (hari) sebelum foto produk dihapus (mig 201). Minimal 30
 * hari — cermin guard `archived_photo_purge_cutoff()` di SQL (nilai < 30 →
 * dianggap 30).
 */
export const ARCHIVED_PHOTO_PURGE_DAY_OPTIONS = [30, 60, 90, 180, 365] as const;
export type ArchivedPhotoPurgeDays =
  (typeof ARCHIVED_PHOTO_PURGE_DAY_OPTIONS)[number];

/**
 * Mode default kartu Biaya Produksi (mig 4250). Sengaja tipe sendiri, bukan
 * `string`: nilai di luar dua ini ditolak parser dan jatuh ke `supplier`.
 */
export type ProductionCostModeSetting = "supplier" | "produksi";

export type SiteSettings = {
  /** Nomor WA toko, disimpan apa adanya (08xx / 62xx) — pakai toWaMeNumber utk link. */
  whatsappNumber: string;
  /** Nama toko (tampilan). */
  storeName: string;
  /** URL logo toko (branding, mig 196). Kosong = belum diunggah. */
  storeLogoUrl: string;
  /** JSON ikon PWA hasil generate dari logo (`parsePwaIcons` di branding.ts). */
  storePwaIcons: string;
  /**
   * Isi `content` meta `google-site-verification` untuk domain toko ini.
   * Kosong = meta-nya TIDAK dipancarkan sama sekali (bukan dipancarkan kosong —
   * meta verifikasi kosong membuat Search Console menolak verifikasinya).
   */
  googleSiteVerification: string;
  /**
   * Isi `content` meta `facebook-domain-verification` (Katalog Instagram) untuk
   * domain toko ini. Kosong = meta-nya tidak dipancarkan.
   */
  metaDomainVerification: string;
  /**
   * Judul situs di tab & hasil pencarian. Kosong = nama toko.
   * MENGGANTIKAN, bukan menambah — lihat `storeSeoTitle`.
   */
  seoTitle: string;
  /**
   * Deskripsi situs di hasil pencarian (beranda) + kalimat tambahan di halaman
   * kategori/merek/cari. Kosong = rumus bawaan.
   */
  seoDescription: string;
  /** Auto-hangus keep ready stock aktif? `false` = keep tidak pernah hangus. */
  keepExpiryEnabled: boolean;
  /** Lama keep ready stock sebelum hangus (hari). */
  keepExpiryDays: KeepExpiryDays;
  /**
   * Tempo bayar nota produk aktif? (mig 4260) `false` = nota tidak pernah
   * kedaluwarsa — dan karena nota aktif menahan sapuan hangus, barangnya ikut
   * tertahan selama nota itu hidup.
   */
  invoiceDueEnabled: boolean;
  /** Lama tempo bayar nota produk (hari) sebelum dibatalkan otomatis. */
  invoiceDueDays: InvoiceDueDays;
  /**
   * Pengajuan nota pembeli langsung terbit (`open`) tanpa Perlu ACC?
   * (mig 4320) `false` (bawaan) = perilaku lama, admin meng-ACC dulu.
   *
   * Menyala pun TIDAK berlaku menyeluruh: nota yang memuat item "harga
   * menyusul", dan "Nota + Kirim" yang ongkirnya belum berangka, tetap
   * jatuh ke Perlu ACC — keduanya masih menunggu keputusan manusia.
   */
  invoiceAutoAccEnabled: boolean;
  /** Biaya dropship/packing aktif? `false` = tidak ada biaya tambahan. */
  dropshipFeeEnabled: boolean;
  /** Nominal biaya dropship/packing (Rp). Diabaikan bila `dropshipFeeEnabled=false`. */
  dropshipFeeAmount: number;
  /**
   * Ambang peringatan Piutang (Rp) di dashboard — kartu memerah bila sisa
   * tagihan MELEWATI angka ini. 0 = peringatan mati (bawaan).
   */
  dashboardAlertPiutang: number;
  /**
   * Ambang peringatan "Closed PO tanpa order" (jumlah produk). 0 = mati.
   */
  dashboardAlertClosedPoNoOrder: number;
  /**
   * Kunci publik VAPID Web Push (aman dipublikasikan). Kosong = pakai env build
   * NEXT_PUBLIC_VAPID_PUBLIC_KEY. Diisi dari Pengaturan Website agar push tetap
   * jalan tanpa rebuild saat env build tak terpasang (kasus migrasi Hostinger).
   */
  vapidPublicKey: string;
  /**
   * Auto-hapus foto produk arsip aktif? (mig 201) `false` = foto tak pernah
   * dihapus otomatis. Default aktif.
   */
  archivedPhotoPurgeEnabled: boolean;
  /** Lama produk arsip (habis + dinonaktifkan sistem) sebelum fotonya dihapus. */
  archivedPhotoPurgeDays: ArchivedPhotoPurgeDays;
  /**
   * Katalog Reseller aktif? (mig 216) `false` = semua link /toko/<slug>
   * menampilkan "tidak tersedia". Default aktif — token "mati" HARUS sama
   * dengan SQL `get_public_reseller_catalog` (kill-switch ditegakkan di RPC).
   */
  resellerCatalogEnabled: boolean;
  /**
   * Pendaftar /daftar langsung AKTIF sebagai pelanggan umum tanpa approval
   * admin? (mig 3810) Default aktif — alur baru: daftar → langsung belanja
   * harga normal → diajak naik level mitra dari /mitra. `false` = perilaku
   * lama (status pending, menunggu approval admin). Dibaca SERVER-SIDE oleh
   * /api/auth/register (per-store); token "mati" harus cocok dengan pembaca
   * route (`registrationAutoActiveFromValue`).
   */
  registrationAutoActive: boolean;
  /**
   * Mode DEFAULT kartu Biaya Produksi (mig 4250): `supplier` = kulak ke vendor
   * bertermin DP, `produksi` = dijahit sendiri (bahan baku / aksesoris / jasa
   * produksi / lain-lain). Hanya DEFAULT — tiap produk bisa menimpanya lewat
   * `products.production_cost_mode`, dan nilai NULL di sana berarti "ikut yang
   * ini". Nilai tak dikenal jatuh ke `supplier` (perilaku sebelum mig 4250),
   * bukan melempar: setelan rusak tak boleh mematikan layar produksi.
   */
  productionCostMode: ProductionCostModeSetting;
  /**
   * Format bawaan tombol "Cetak Tagihan" (Detail Tagihan) & "Cetak Surat Jalan"
   * (mig 7400): Printer Biasa (PDF A4 / surat jalan A5-A4) atau Printer Thermal
   * (struk 58/80 mm). Hanya PILIHAN AWAL saklar format di layar cetak — format
   * lain tetap bisa dipilih saat mencetak. Tak dikenal → `regular`.
   */
  printModeInvoice: PrintMode;
  printModePackingList: PrintMode;

  // ── Direktori Mitra Terdekat (mig 4230) ───────────────────────────────────
  /**
   * Halaman publik `/cari-mitra` menyala. Kill-switch sesungguhnya ditegakkan
   * DI DALAM RPC (satu pintu, cermin resellerCatalogEnabled); nilai di sini
   * hanya untuk layar admin.
   */
  partnerDirectoryEnabled: boolean;
  /**
   * Tombol "Beli lewat mitra terdekat" di halaman produk. Default MATI: ia
   * mengubah CTA halaman yang sudah tayang dan mengalihkan pembeli dari toko ke
   * reseller — keputusan bisnis pemilik toko, bukan efek samping deploy.
   */
  partnerProductCtaEnabled: boolean;
  /** Pencatatan impresi & klik kartu mitra (kartu statistik di /account). */
  partnerDirectoryStatsEnabled: boolean;

  // ── Tombol "Order via WhatsApp" untuk tamu ────────────────────────────────
  /**
   * Tombol "Order via WhatsApp" di halaman produk untuk pengunjung yang belum
   * login — tujuannya nomor "Hubungi Admin" (`whatsappNumber`) di kartu yang
   * sama pada layar setelan.
   *
   * Default NYALA, kebalikan `partnerProductCtaEnabled` di atas, dan bedanya
   * bukan selera: tombol itu belum pernah tayang saat saklarnya lahir,
   * sedangkan tombol INI sudah hidup di semua toko sebelum saklarnya ada.
   * Default mati berarti satu deploy mencabut jalur pesan satu-satunya milik
   * pengunjung tanpa akun — tanpa ada pemilik toko yang memutuskannya.
   */
  guestWaOrderEnabled: boolean;

  // ── Izin clone produk antar tenant (mig 5850) ─────────────────────────────
  /**
   * Toko LAIN di Kelolify boleh meng-clone produk toko ini?
   *
   * Default NYALA (opt-out), putusan pemilik — sejalan dengan direktori toko
   * (mig 5780): opt-in terdengar lebih sopan tapi praktis nol efek, karena
   * kebanyakan pemilik tak akan pernah menyalakannya dan penyuplai tetap jadi
   * pulau terputus dari resellernya sendiri.
   *
   * Yang menyeberang hanya nama, deskripsi, varian, harga, berat, dan gambar.
   * HPP, diskon, SKU, stok, dan DP TIDAK PERNAH ikut — itulah yang membuat
   * default menyala bisa dipertanggungjawabkan.
   *
   * Kill-switch sesungguhnya ditegakkan DI DALAM RPC (satu pintu, cermin
   * resellerCatalogEnabled); nilai di sini untuk layar admin.
   */
  productCloneAllowed: boolean;

  /**
   * Sisa PO otomatis jadi Ready Stok (mig 7380): pcs PO yang sudah datang lalu
   * pesanannya dibatalkan langsung dipindah ke varian Ready ber-SKU sama, atau
   * ke kembaran "<nama> (Ready)" produk PO-nya. Bawaan NYALA (putusan pemilik
   * 2026-10-15). Yang menegakkannya trigger buku Sisa PO di SQL; nilai di sini
   * untuk layar admin.
   */
  poSurplusAutoReady: boolean;

  // ── Cek ongkir otomatis (Biteship, mig 3720) ──────────────────────────────
  /**
   * Cek ongkir otomatis aktif? Default MATI — toko tidak berubah perilakunya
   * sampai adminnya mengisi lokasi toko lalu menyalakannya sendiri.
   */
  shippingRateEnabled: boolean;
  /** `area_id` Biteship lokasi toko (asal kirim). "" = belum diisi. */
  shippingOriginAreaId: string;
  /** `area_id` dipotong sebelum `IDZ…` — kunci cache tarif. */
  shippingOriginDistrictKey: string;
  /** Label kecamatan asal untuk ditampilkan ("Pesanggrahan, Jakarta Selatan, …"). */
  shippingOriginLabel: string;
  /**
   * Kode kurir aktif — kanonik (lower, unik, urut) karena ditulis lewat
   * `couriersKey`. **Kosong = fitur tak bisa menyala**: route `/api/shipping/rates`
   * menjawab `{enabled:false}` saat daftar kurir kosong, jadi layar setelan
   * wajib menghalangi keadaan itu alih-alih membiarkannya mati senyap.
   */
  shippingCouriers: string[];
  /**
   * Toleransi berat (gram) sebelum pembulatan ke kilo berikutnya (mig 3760).
   * 0 = tanpa toleransi (perilaku sebelum mig 3760).
   *
   * ⚠️ Angka ini MENGURANGI ongkir yang ditampilkan tanpa mengurangi yang
   * ditagihkan kurir — selisihnya ditanggung toko. Layar yang menyuntingnya
   * WAJIB mengatakan itu (lihat `weightToleranceWarning` di biteship.ts).
   *
   * Sudah TERJEPIT 0–999 saat dibaca (`clampWeightToleranceGram` di dalam
   * `parseShippingOriginSettings`) — jangan menjepitnya lagi dengan aturan
   * sendiri di layar, karena dua penjepit yang berbeda membuat admin melihat
   * satu angka sementara route menghitung dengan angka lain.
   */
  shippingWeightToleranceGram: number;
  /**
   * Multigudang (mig 7200): ongkir dihitung dari gudang tiap produk. Default
   * MATI — saklar mati = perilaku persis sebelum mig 7200 (semua dari lokasi
   * toko). Token "hidup" SAMA PERSIS dengan `multi_warehouse_enabled()` di SQL,
   * karena gerbang kutipan (`consume_shipping_quote`) ikut membacanya.
   */
  multiWarehouseEnabled: boolean;

  // ── Pixel iklan (halaman /admin/settings/pixel) ───────────────────────────
  // Nilai di sini APA ADANYA dari DB (trim saja) — SENGAJA tidak disaring
  // validator: form admin harus bisa MENAMPILKAN nilai tersimpan yang invalid
  // (beserta peringatannya), bukan menyembunyikannya sampai fitur tampak mati
  // tanpa sebab. Yang wajib tersaring adalah jalur render storefront
  // (`fetchPixelSettingsServer` → `sanitizeStorePixelIds`).
  /** Meta Pixel ID (15–16 digit). "" = tidak dipakai. */
  metaPixelId: string;
  /** TikTok Pixel ID (kapital alfanumerik ±20 char). "" = tidak dipakai. */
  tiktokPixelId: string;
  /** GA4 Measurement ID (G-XXXXXXXXXX). "" = tidak dipakai. */
  ga4MeasurementId: string;
  /** GTM Container ID (GTM-XXXXXXX). "" = tidak dipakai. */
  gtmContainerId: string;

  /**
   * Banner privasi/cookie (UU PDP) aktif? Default MATI — pemberitahuan
   * INFORMATIF yang bisa ditutup, TIDAK memblokir pixel (keputusan produk:
   * banner opt-in yang mematikan pixel akan melumpuhkan data iklan penyewa;
   * bila kelak butuh consent sungguhan, itu fitur terpisah).
   */
  privacyBannerEnabled: boolean;
  /** Teks banner kustom. "" = pakai teks bawaan (DEFAULT_PRIVACY_BANNER_TEXT). */
  privacyBannerText: string;
  /** Dropship Shopee mitra aktif? Default MATI (fitur baru tak menyala sendiri). */
  dropshipShopeeEnabled: boolean;
  /** Cadangan stok (pcs) yang TIDAK ikut dikirim ke file Shopee mitra. */
  dropshipShopeeStockBuffer: number;
  /** Markup minimum (%) yang ditegakkan server pada unduhan mitra. */
  dropshipShopeeMarkupMin: number;
  /** Hanya mitra bergrup aktif (Reseller/Agen) yang boleh; default NYALA. */
  dropshipShopeeMitraOnly: boolean;
};

/** Teks bawaan banner privasi — dipakai saat `privacy_banner_text` kosong. */
export const DEFAULT_PRIVACY_BANNER_TEXT =
  "Situs ini memakai cookie dan piksel iklan untuk meningkatkan pengalaman " +
  "belanja serta mengukur promosi. Dengan melanjutkan, kamu menyetujui " +
  "penggunaan tersebut.";

/** Default dipakai sebelum data termuat / bila setelan kosong. */
export const DEFAULT_SITE_SETTINGS: SiteSettings = {
  whatsappNumber: ADMIN_WHATSAPP,
  storeName: "Kelolify",
  storeLogoUrl: "",
  storePwaIcons: "",
  googleSiteVerification: "",
  metaDomainVerification: "",
  seoTitle: "",
  seoDescription: "",
  keepExpiryEnabled: true,
  keepExpiryDays: 7,
  invoiceDueEnabled: true,
  invoiceDueDays: 3,
  invoiceAutoAccEnabled: false,
  dropshipFeeEnabled: false,
  dropshipFeeAmount: 0,
  dashboardAlertPiutang: 0,
  dashboardAlertClosedPoNoOrder: 0,
  vapidPublicKey: "",
  archivedPhotoPurgeEnabled: true,
  archivedPhotoPurgeDays: 30,
  resellerCatalogEnabled: true,
  registrationAutoActive: true,
  productionCostMode: "supplier",
  printModeInvoice: "regular",
  printModePackingList: "regular",
  partnerDirectoryEnabled: true,
  partnerProductCtaEnabled: false,
  partnerDirectoryStatsEnabled: true,
  guestWaOrderEnabled: true,
  productCloneAllowed: true,
  poSurplusAutoReady: true,
  shippingRateEnabled: false,
  shippingOriginAreaId: "",
  shippingOriginDistrictKey: "",
  shippingOriginLabel: "",
  // SENGAJA kosong, BUKAN daftar kurir bawaan mig 3720. Sisi server
  // (`parseShippingOriginSettings`, dipakai route) memulangkan [] saat barisnya
  // tak ada, dan default klien yang berbeda akan membuat kartu setelan
  // menampilkan tujuh kurir tercentang sementara route menjawab
  // `{enabled:false}` — persis kelas bug "dua pembaca tak sepakat" yang
  // diperingatkan biteship.ts. Toko tanpa baris ini (provisioning setelah mig
  // 3720 — `admin_provision_store` tak menyemainya) memang harus memilih
  // kurirnya sendiri, dan kartu setelan yang menghalangi toggle saat kosong
  // membuat keharusan itu terlihat.
  shippingCouriers: [],
  // 0 = perilaku sebelum mig 3760 (tanpa toleransi). SENGAJA nol: toleransi
  // membuat toko menyubsidi selisih ongkir, dan itu harus jadi keputusan sadar
  // admin — bukan bawaan yang menyala sendiri saat migrasi tayang.
  shippingWeightToleranceGram: 0,
  multiWarehouseEnabled: false,
  // Pixel iklan default MATI — tiap toko memasang ID-nya sendiri.
  metaPixelId: "",
  tiktokPixelId: "",
  ga4MeasurementId: "",
  gtmContainerId: "",
  privacyBannerEnabled: false,
  privacyBannerText: "",
  dropshipShopeeEnabled: false,
  dropshipShopeeStockBuffer: 0,
  dropshipShopeeMarkupMin: 0,
  dropshipShopeeMitraOnly: true,
};

/** Baris mentah `site_settings` (key,value). */
export type SiteSettingRow = { key: string; value: string | null };

/** Normalisasi nominal biaya dropship/packing mentah → angka ≥ 0 (fallback 0). */
export function normalizeDropshipFeeAmount(raw: string | null): number {
  const n = Number.parseInt((raw ?? "0").trim(), 10);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

/** Normalisasi nilai hari mentah ke salah satu opsi (fallback default). */
export function normalizeKeepExpiryDays(raw: string | null): KeepExpiryDays {
  const n = Number.parseInt((raw ?? "").trim(), 10);
  return (KEEP_EXPIRY_DAY_OPTIONS as readonly number[]).includes(n)
    ? (n as KeepExpiryDays)
    : DEFAULT_SITE_SETTINGS.keepExpiryDays;
}

/** Normalisasi tempo nota (hari) mentah ke salah satu opsi (fallback default). */
export function normalizeInvoiceDueDays(raw: string | null): InvoiceDueDays {
  const n = Number.parseInt((raw ?? "").trim(), 10);
  return (INVOICE_DUE_DAY_OPTIONS as readonly number[]).includes(n)
    ? (n as InvoiceDueDays)
    : DEFAULT_SITE_SETTINGS.invoiceDueDays;
}

/** Normalisasi lama arsip (hari) mentah ke salah satu opsi (fallback default). */
export function normalizeArchivedPhotoPurgeDays(
  raw: string | null
): ArchivedPhotoPurgeDays {
  const n = Number.parseInt((raw ?? "").trim(), 10);
  return (ARCHIVED_PHOTO_PURGE_DAY_OPTIONS as readonly number[]).includes(n)
    ? (n as ArchivedPhotoPurgeDays)
    : DEFAULT_SITE_SETTINGS.archivedPhotoPurgeDays;
}

/**
 * Tafsir nilai mentah `registration_auto_active` (mig 3810) — SATU penafsir
 * untuk klien (mapSettingsRows) DAN server (/api/auth/register). Default
 * AKTIF: hanya token "mati" eksplisit yang mengembalikan alur approval lama.
 * Dua pembaca dengan token berbeda = admin melihat toggle NYALA sementara
 * pendaftar tetap menggantung pending (kelas bug "dua pembaca tak sepakat").
 */
export function registrationAutoActiveFromValue(
  raw: string | null | undefined
): boolean {
  if (raw === undefined || raw === null || raw.trim() === "") {
    return DEFAULT_SITE_SETTINGS.registrationAutoActive;
  }
  return !["false", "0", "off", "no"].includes(raw.trim().toLowerCase());
}

/** Petakan baris key-value → objek SiteSettings (kosong → default). */
export function mapSettingsRows(rows: SiteSettingRow[]): SiteSettings {
  const map = new Map(rows.map((r) => [r.key, r.value ?? ""]));
  const wa = (map.get(SITE_SETTING_KEYS.whatsappNumber) ?? "").trim();
  const store = (map.get(SITE_SETTING_KEYS.storeName) ?? "").trim();

  const keepRaw = map.get(SITE_SETTING_KEYS.keepExpiryEnabled);
  // Default aktif: hanya nilai "mati" eksplisit yang mematikan (cermin SQL).
  const keepEnabled =
    keepRaw === undefined
      ? DEFAULT_SITE_SETTINGS.keepExpiryEnabled
      : !["false", "0", "off", "no"].includes(keepRaw.trim().toLowerCase());

  const invoiceDueRaw = map.get(SITE_SETTING_KEYS.invoiceDueEnabled);
  // Default aktif; daftar token "mati" SAMA PERSIS dengan `invoice_due_at` di
  // SQL (mig 4260). Dua pembaca dengan token berbeda = admin melihat toggle
  // NYALA sementara notanya tak pernah kedaluwarsa.
  const invoiceDueEnabled =
    invoiceDueRaw === undefined
      ? DEFAULT_SITE_SETTINGS.invoiceDueEnabled
      : !["false", "0", "off", "no"].includes(invoiceDueRaw.trim().toLowerCase());

  const autoAccRaw = map.get(SITE_SETTING_KEYS.invoiceAutoAccEnabled);
  // Default MATI: hanya nilai "hidup" eksplisit yang mengaktifkan (pola
  // dropshipFeeEnabled — fitur baru tak boleh menyala sendiri). Daftar token
  // SAMA PERSIS dengan `invoice_auto_acc_enabled(p_store)` di SQL (mig 4320).
  const invoiceAutoAccEnabled =
    autoAccRaw === undefined
      ? DEFAULT_SITE_SETTINGS.invoiceAutoAccEnabled
      : ["true", "1", "on", "yes"].includes(autoAccRaw.trim().toLowerCase());

  const dropshipRaw = map.get(SITE_SETTING_KEYS.dropshipFeeEnabled);
  // Default mati: hanya nilai "hidup" eksplisit yang mengaktifkan.
  const dropshipFeeEnabled =
    dropshipRaw === undefined
      ? DEFAULT_SITE_SETTINGS.dropshipFeeEnabled
      : ["true", "1", "on", "yes"].includes(dropshipRaw.trim().toLowerCase());

  const purgeRaw = map.get(SITE_SETTING_KEYS.archivedPhotoPurgeEnabled);
  // Default aktif: hanya nilai "mati" eksplisit yang mematikan (cermin SQL 201).
  const archivedPhotoPurgeEnabled =
    purgeRaw === undefined
      ? DEFAULT_SITE_SETTINGS.archivedPhotoPurgeEnabled
      : !["false", "0", "off", "no"].includes(purgeRaw.trim().toLowerCase());

  // Ketiga bendera direktori mitra memakai pembaca yang SAMA dengan katalog
  // reseller, jadi daftar token "mati"-nya mustahil menyimpang antar-fitur.
  const flagOn = (key: string, fallback: boolean): boolean => {
    const raw = map.get(key);
    return raw === undefined || raw.trim() === ""
      ? fallback
      : !["false", "0", "off", "no"].includes(raw.trim().toLowerCase());
  };

  // Mode biaya produksi (mig 4250): whitelist ketat, apa pun selain kedua nilai
  // itu — termasuk string kosong dan setelan lama yang tak ada — jatuh ke
  // `supplier`, yaitu perilaku persis sebelum fitur ini ada.
  const modeRaw = (map.get(SITE_SETTING_KEYS.productionCostMode) ?? "")
    .trim()
    .toLowerCase();
  const productionCostMode: ProductionCostModeSetting =
    modeRaw === "produksi" ? "produksi" : "supplier";

  const catalogRaw = map.get(SITE_SETTING_KEYS.resellerCatalogEnabled);
  // Default aktif: token "mati" cermin SQL get_public_reseller_catalog (mig 216).
  const resellerCatalogEnabled =
    catalogRaw === undefined
      ? DEFAULT_SITE_SETTINGS.resellerCatalogEnabled
      : !["false", "0", "off", "no"].includes(catalogRaw.trim().toLowerCase());

  // Keenam setelan cek ongkir DIBACA lewat parser yang SAMA dengan sisi server
  // (`readOriginSettings` → `parseShippingOriginSettings`), bukan diurai ulang
  // di sini. Token "hidup"-nya (`true/1/on/yes`) dengan begitu mustahil
  // menyimpang: kalau kartu setelan dan route menafsirkan "1" berbeda, admin
  // melihat toggle NYALA sementara pembeli tak pernah melihat satu pun tarif.
  const shipping = parseShippingOriginSettings(rows);

  return {
    whatsappNumber: wa || DEFAULT_SITE_SETTINGS.whatsappNumber,
    storeName: store || DEFAULT_SITE_SETTINGS.storeName,
    storeLogoUrl: (map.get(SITE_SETTING_KEYS.storeLogoUrl) ?? "").trim(),
    storePwaIcons: (map.get(SITE_SETTING_KEYS.storePwaIcons) ?? "").trim(),
    googleSiteVerification: (
      map.get(SITE_SETTING_KEYS.googleSiteVerification) ?? ""
    ).trim(),
    metaDomainVerification: (
      map.get(SITE_SETTING_KEYS.metaDomainVerification) ?? ""
    ).trim(),
    seoTitle: (map.get(SITE_SETTING_KEYS.seoTitle) ?? "").trim(),
    seoDescription: (map.get(SITE_SETTING_KEYS.seoDescription) ?? "").trim(),
    keepExpiryEnabled: keepEnabled,
    keepExpiryDays: normalizeKeepExpiryDays(
      map.get(SITE_SETTING_KEYS.keepExpiryDays) ?? null
    ),
    invoiceDueEnabled,
    invoiceAutoAccEnabled,
    invoiceDueDays: normalizeInvoiceDueDays(
      map.get(SITE_SETTING_KEYS.invoiceDueDays) ?? null
    ),
    dropshipFeeEnabled,
    dropshipFeeAmount: normalizeDropshipFeeAmount(
      map.get(SITE_SETTING_KEYS.dropshipFeeAmount) ?? null
    ),
    dashboardAlertPiutang: normalizeDropshipFeeAmount(
      map.get(SITE_SETTING_KEYS.dashboardAlertPiutang) ?? null
    ),
    dashboardAlertClosedPoNoOrder: normalizeDropshipFeeAmount(
      map.get(SITE_SETTING_KEYS.dashboardAlertClosedPoNoOrder) ?? null
    ),
    vapidPublicKey: (map.get(SITE_SETTING_KEYS.vapidPublicKey) ?? "").trim(),
    archivedPhotoPurgeEnabled,
    archivedPhotoPurgeDays: normalizeArchivedPhotoPurgeDays(
      map.get(SITE_SETTING_KEYS.archivedPhotoPurgeDays) ?? null
    ),
    resellerCatalogEnabled,
    productionCostMode,
    printModeInvoice: coercePrintMode(map.get(SITE_SETTING_KEYS.printModeInvoice)),
    printModePackingList: coercePrintMode(map.get(SITE_SETTING_KEYS.printModePackingList)),
    partnerDirectoryEnabled: flagOn(
      SITE_SETTING_KEYS.partnerDirectoryEnabled,
      DEFAULT_SITE_SETTINGS.partnerDirectoryEnabled
    ),
    partnerProductCtaEnabled: flagOn(
      SITE_SETTING_KEYS.partnerProductCtaEnabled,
      DEFAULT_SITE_SETTINGS.partnerProductCtaEnabled
    ),
    partnerDirectoryStatsEnabled: flagOn(
      SITE_SETTING_KEYS.partnerDirectoryStatsEnabled,
      DEFAULT_SITE_SETTINGS.partnerDirectoryStatsEnabled
    ),
    // `flagOn`, BUKAN daftar token "hidup" seperti privacyBannerEnabled di
    // bawah: pembacanya harus memulangkan NYALA untuk baris yang belum ada.
    // Tak ada migrasi yang menyemai key ini (desain key-value mig 083), jadi
    // sampai admin menyimpan setelan pertama kalinya SEMUA toko tidak punya
    // barisnya — dan daftar token "hidup" akan membaca ketiadaan itu sebagai
    // MATI, yaitu tepat pencabutan senyap yang dihindari default NYALA.
    guestWaOrderEnabled: flagOn(
      SITE_SETTING_KEYS.guestWaOrderEnabled,
      DEFAULT_SITE_SETTINGS.guestWaOrderEnabled
    ),
    // Alasan `flagOn` identik dengan guestWaOrderEnabled di atas: tak ada
    // migrasi yang menyemai key ini, jadi ketiadaan barisnya WAJIB terbaca
    // NYALA — dan kembarannya di SQL (`store_flag_on`, mig 5850) memakai
    // default yang sama supaya layar admin dan RPC tak pernah berbeda pendapat.
    productCloneAllowed: flagOn(
      SITE_SETTING_KEYS.productCloneAllowed,
      DEFAULT_SITE_SETTINGS.productCloneAllowed
    ),
    // Tak ada migrasi yang menyemai key ini: baris absen WAJIB terbaca NYALA,
    // sama dengan `store_flag_on(…, true)` di trigger mig 7380.
    poSurplusAutoReady: flagOn(
      SITE_SETTING_KEYS.poSurplusAutoReady,
      DEFAULT_SITE_SETTINGS.poSurplusAutoReady
    ),
    registrationAutoActive: registrationAutoActiveFromValue(
      map.get(SITE_SETTING_KEYS.registrationAutoActive)
    ),
    shippingRateEnabled: shipping.enabled,
    shippingOriginAreaId: shipping.originAreaId,
    shippingOriginDistrictKey: shipping.originDistrictKey,
    shippingOriginLabel: shipping.originLabel,
    shippingCouriers: shipping.couriers,
    shippingWeightToleranceGram: shipping.weightToleranceGram,
    multiWarehouseEnabled: parseMultiWarehouseFlag(
      map.get(SITE_SETTING_KEYS.multiWarehouseEnabled)
    ),
    metaPixelId: (map.get(SITE_SETTING_KEYS.metaPixelId) ?? "").trim(),
    tiktokPixelId: (map.get(SITE_SETTING_KEYS.tiktokPixelId) ?? "").trim(),
    ga4MeasurementId: (map.get(SITE_SETTING_KEYS.ga4MeasurementId) ?? "").trim(),
    gtmContainerId: (map.get(SITE_SETTING_KEYS.gtmContainerId) ?? "").trim(),
    // Default mati: hanya token "hidup" eksplisit yang menyalakan (pola
    // dropshipFeeEnabled — fitur baru tak boleh menyala sendiri).
    privacyBannerEnabled: ["true", "1", "on", "yes"].includes(
      (map.get(SITE_SETTING_KEYS.privacyBannerEnabled) ?? "").trim().toLowerCase()
    ),
    privacyBannerText: (map.get(SITE_SETTING_KEYS.privacyBannerText) ?? "").trim(),
    // Dropship Shopee (mig 4720): toggle default MATI — token "hidup" SAMA
    // PERSIS dengan `dropship_shopee_access()` di SQL; "hanya mitra" default
    // NYALA — hanya token "mati" eksplisit yang melonggarkannya (pembaca
    // `flagOn`, token mati SAMA dengan SQL).
    dropshipShopeeEnabled: ["true", "1", "on", "yes"].includes(
      (map.get(SITE_SETTING_KEYS.dropshipShopeeEnabled) ?? "").trim().toLowerCase()
    ),
    dropshipShopeeStockBuffer: normalizeDropshipStockBuffer(
      map.get(SITE_SETTING_KEYS.dropshipShopeeStockBuffer) ?? null
    ),
    dropshipShopeeMarkupMin: normalizeDropshipMarkupMin(
      map.get(SITE_SETTING_KEYS.dropshipShopeeMarkupMin) ?? null
    ),
    dropshipShopeeMitraOnly: flagOn(
      SITE_SETTING_KEYS.dropshipShopeeMitraOnly,
      DEFAULT_SITE_SETTINGS.dropshipShopeeMitraOnly
    ),
  };
}

/**
 * Normalisasi nomor apa pun (08xx / +62xx / 62xx) ke format wa.me: 62xxx tanpa
 * "+". Mengembalikan string kosong bila tak ada digit.
 */
export function toWaMeNumber(raw: string): string {
  const local = normalizePhone(raw); // → 08xx (atau apa adanya bila bukan 0/62)
  const digits = local.replace(/[^0-9]/g, "");
  if (!digits) return "";
  if (digits.startsWith("0")) return "62" + digits.slice(1);
  return digits;
}

/**
 * Validasi input nomor WA dari form admin. Kembalikan pesan error (string) bila
 * tidak valid, atau `null` bila valid. Boleh kosong? Tidak — nomor WA wajib.
 */
export function validateWhatsappInput(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return "Nomor WhatsApp wajib diisi.";
  if (!isValidPhone(trimmed))
    return "Format nomor tidak valid (gunakan 08xx atau +62xx, min. 10 digit).";
  return null;
}
